import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";

import CommandItem from "./CommandItem";

export interface ContextMenuHandle {
  onKeyDown: (event: KeyboardEvent) => boolean;
}

interface ContextMenuProps {
  items: {
    title: string;
    description?: string;
    icon?: string;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    command: (props: any) => void;
  }[];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  command: (item: any) => void;
}

const ContextMenu = forwardRef<ContextMenuHandle, ContextMenuProps>(({ items, command }, ref) => {
  const [selectedIndex, setSelectedIndex] = useState(0);
  const itemRefs = useRef<(HTMLDivElement | null)[]>([]);
  const containerRef = useRef<HTMLDivElement>(null);

  const selectItem = (index: number) => {
    const item = items[index];
    if (item) {
      command(item);
    }
  };

  const scrollIntoView = (index: number) => {
    const element = itemRefs.current[index];
    const container = containerRef.current;

    if (element && container) {
      // For first element, scroll to container start
      if (index === 0) {
        container.scrollTop = 0;
        return;
      }

      // For last element, scroll to container end
      if (index === items.length - 1) {
        container.scrollTop = container.scrollHeight - container.clientHeight;
        return;
      }

      const elementRect = element.getBoundingClientRect();
      const containerRect = container.getBoundingClientRect();

      // Check if element is above visible area
      if (elementRect.top < containerRect.top) {
        element.scrollIntoView({ block: "start", behavior: "smooth" });
      }
      // Check if element is below visible area
      else if (elementRect.bottom > containerRect.bottom) {
        element.scrollIntoView({ block: "end", behavior: "smooth" });
      }
    }
  };

  const upHandler = () => {
    const newIndex = (selectedIndex + items.length - 1) % items.length;
    setSelectedIndex(newIndex);
    scrollIntoView(newIndex);
  };

  const downHandler = () => {
    const newIndex = (selectedIndex + 1) % items.length;
    setSelectedIndex(newIndex);
    scrollIntoView(newIndex);
  };

  const enterHandler = () => {
    selectItem(selectedIndex);
  };

  useEffect(() => {
    setSelectedIndex(0);
    itemRefs.current = itemRefs.current.slice(0, items.length);
  }, [items]);

  useImperativeHandle(ref, () => ({
    onKeyDown: (event: KeyboardEvent) => {
      if (event.key === "ArrowUp") {
        upHandler();
        return true;
      }

      if (event.key === "ArrowDown") {
        downHandler();
        return true;
      }

      if (event.key === "Enter") {
        enterHandler();
        return true;
      }

      return false;
    },
  }));

  return (
    <div
      ref={containerRef}
      className="z-50 h-auto max-h-80 w-72 overflow-auto rounded-xl border border-gray-200/50 bg-white/95 p-1.5 shadow-xl shadow-gray-900/5 ring-1 ring-gray-900/5 backdrop-blur-sm"
    >
      {items.length ? (
        <div className="space-y-0.5">
          {items.map((item, index) => (
            <CommandItem
              key={item.title}
              ref={el => (itemRefs.current[index] = el)}
              title={item.title}
              description={item.description}
              onSelect={() => selectItem(index)}
              isSelected={index === selectedIndex}
              index={index}
            />
          ))}
        </div>
      ) : (
        <div className="px-3 py-8 text-center text-sm text-gray-500">No results found</div>
      )}
    </div>
  );
});

ContextMenu.displayName = "ContextMenu";

export default ContextMenu;
